import { DatePipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import {
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepicker,
    MatDatepickerActions,
    MatDatepickerApply,
    MatDatepickerCancel,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatEndDate,
    MatStartDate,
} from '@angular/material/datepicker';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatList, MatListItem } from '@angular/material/list';
import { MatOption, MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMatTimepickerComponent } from '@ngxmc/datetime-picker';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Calendar } from 'src/app/models/centrex/calendar';
import { DaysOfWeek } from 'src/app/models/centrex/enums/days-of-week';
import { Instance } from 'src/app/models/centrex/instance';
import { TranslateDatePipe } from 'src/app/pipes/translate-date.pipe';
import { CalendarService } from 'src/app/services/centrex/calendar.service';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { OrganizationsService } from 'src/app/services/organizations.service';

export enum CalendarType {
    Periodic = 'Periodic',
    Dates = 'Dates',
}

@Component({
    selector: 'app-calendar-form',
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatDatepickerInput,
        MatHint,
        MatDatepickerToggle,
        MatSuffix,
        MatDatepicker,
        MatDatepickerActions,
        MatButton,
        MatDatepickerCancel,
        MatDatepickerApply,
        MatList,
        MatListItem,
        MatIconButton,
        MatIcon,
        MatDateRangeInput,
        MatStartDate,
        MatEndDate,
        MatDateRangePicker,
        NgxMatTimepickerComponent,
        RouterLink,
        TranslateModule,
        TranslateDatePipe,
        FormsModule,
        MatCheckbox,
    ],
    templateUrl: './calendar-form.component.html',
    styleUrl: './calendar-form.component.scss',
})
export class CalendarFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    calendarTypes = Object.keys(CalendarType);
    currentCalendarType: CalendarType = CalendarType.Periodic;

    calendarInformations: Calendar;
    calendarForm: UntypedFormGroup;
    isCreation: boolean;
    daysOfWeek = Object.keys(DaysOfWeek);
    dates: Date[] = [];
    periods: { start: Date; end: Date }[] = [];
    hours: { start: Date; end: Date }[] = [];
    dateFormat: string = 'JJ/MM/AAAA';
    week = [
        { name: 'monday', checked: false },
        { name: 'tuesday', checked: false },
        { name: 'wednesday', checked: false },
        { name: 'thursday', checked: false },
        { name: 'friday', checked: false },
        { name: 'saturday', checked: false },
        { name: 'sunday', checked: false },
    ];
    allDays: boolean = false;
    weekDays: boolean = false;
    weekEnds: boolean = false;

    @ViewChild('allDaysCheckbox') allDaysCheckbox: MatCheckbox;
    @ViewChild('weekDaysCheckbox') weekDaysCheckbox: MatCheckbox;
    @ViewChild('weekEndsCheckbox') weekEndsCheckbox: MatCheckbox;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private calendarService: CalendarService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private datePipe: DatePipe,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.createForm({} as Calendar);

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/calendars']);
            }
        });
    }

    ngOnInit(): void {
        let calendarId: string;

        // Récupère l'identifiant du calendrier
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            calendarId = params['id'];
        });

        this.loadDatas(calendarId);

        this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.dateFormat = this.translate.currentLang === 'fr' ? 'JJ/MM/AAAA' : 'MM/JJ/AAAA';
        });
    }

    loadDatas(calendarId: string) {
        this.spinner.show();

        if (calendarId) {
            this.calendarService
                .getCalendar(calendarId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (calendar) => {
                        this.calendarInformations = calendar;

                        // Affecte les jours
                        if (calendar.days === 'allDays') {
                            this.allDays = true;
                            this.week.forEach((a) => (a.checked = true));
                        } else if (calendar.days === 'weekDays') {
                            this.weekDays = true;
                            this.week.filter((a) => a.name !== 'saturday' && a.name !== 'sunday').forEach((a) => (a.checked = true));
                        } else if (calendar.days === 'weekEnds') {
                            this.weekEnds = true;
                            this.week.filter((a) => a.name === 'saturday' || a.name === 'sunday').forEach((a) => (a.checked = true));
                        } else {
                            calendar.days.split(',').forEach((a) => {
                                this.week.find((x) => x.name === a.trim()).checked = true;
                            });
                        }

                        // Récupération des heures du calendrier
                        calendar.hours.forEach((hourPeriod) => {
                            let period = hourPeriod.split('-');
                            let startTime = period[0].split(':');
                            let endTime = period[1].split(':');
                            this.hours.push({
                                start: new Date(0, 0, 0, Number.parseInt(startTime[0]), Number.parseInt(startTime[1])),
                                end: new Date(0, 0, 0, Number.parseInt(endTime[0]), Number.parseInt(endTime[1])),
                            });
                        });

                        let startDate: Date = null;
                        let isPeriod: boolean = false;

                        let hasPeriods: boolean = false;
                        let hasDates: boolean = false;
                        let currentDates: Date[] = [];

                        for (let index = 0; index <= calendar.dates.length - 1; index++) {
                            let currentDate = new Date(calendar.dates[index]);
                            let nextDate: Date = index + 1 <= calendar.dates.length - 1 ? new Date(calendar.dates[index + 1]) : null;
                            currentDates.push(currentDate);

                            if (
                                nextDate &&
                                this.datePipe.transform(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1), 'shortDate') ===
                                    this.datePipe.transform(nextDate, 'shortDate')
                            ) {
                                isPeriod = true;
                                if (startDate === null) {
                                    startDate = currentDate;
                                }
                            } else {
                                if (isPeriod === true) {
                                    this.periods.push({
                                        start: startDate,
                                        end: currentDate,
                                    });
                                    startDate = null;
                                    isPeriod = false;
                                    hasPeriods = true;
                                } else {
                                    isPeriod = false;
                                    hasDates = true;
                                    this.dates.push(currentDate);
                                }
                            }
                        }

                        if (hasPeriods === true && hasDates === true) {
                            this.dates = currentDates;
                            this.periods = [];
                            this.currentCalendarType = CalendarType.Dates;
                        } else if (hasPeriods === false && hasDates === true) {
                            this.currentCalendarType = CalendarType.Dates;
                        } else {
                            this.currentCalendarType = CalendarType.Periodic;
                        }

                        this.isCreation = false;

                        // Créé le formulaire
                        this.createForm(this.calendarInformations);
                    },
                    error: () => {
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;

            this.allDays = true;
            this.week.forEach((a) => (a.checked = true));

            this.calendarInformations = new Calendar();
            this.calendarInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';

            // Créé le formulaire
            this.createForm(this.calendarInformations);
        }
    }

    createForm(calendar: Calendar) {
        this.calendarForm = this.formbuilder.group({
            id: [calendar.id],
            organizationId: [calendar.organizationId],
            name: [calendar.name],
            nextId: [calendar.nextId],
            date: [],
            startPeriod: [],
            endPeriod: [],
            startHour: [new Date(0, 0, 0, 0, 0)],
            endHour: [new Date(0, 0, 0, 1, 0)],
        });

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.calendarService
                .addCalendar(this.calendarInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (deviceCreated) => {
                        this.toastr.success(this.translate.instant('Calendar.SuccessMessageCreateCalendar'));
                        // Redirige sur la page des périphériques
                        this.router.navigate(['/calendars']);
                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Calendar.ErrorMessageCreateCalendar'));
                        this.spinner.hide();
                    },
                });
        } else {
            this.calendarService
                .updateCalendar(this.calendarInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (calendar) => {
                        this.toastr.success(this.translate.instant('Calendar.SuccessMessageUpdateCalendar'));
                        this.router.navigate(['/calendars']);
                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Calendar.ErrorMessageUpdateCalendar'));
                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.calendarInformations.name = this.calendarForm.value.name;

        let daysValue: string = '';

        if (this.currentCalendarType === CalendarType.Periodic) {
            this.week
                .filter((a) => a.checked === true)
                .forEach((a) => {
                    daysValue += a.name + ',';
                });

            daysValue = daysValue.substring(0, daysValue.length - 1);
        } else {
            // Pour les dates spécifiques utilisation de "allDays" pour être sur de ne pas avoir d'erreurs
            daysValue = DaysOfWeek.allDays;
        }

        this.calendarInformations.days = daysValue;

        this.calendarInformations.dates = [];

        if (this.dates.length > 0) {
            this.dates.forEach((date) => {
                this.calendarInformations.dates.push(this.datePipe.transform(date, 'yyyy-MM-dd'));
            });
        }

        if (this.periods.length > 0) {
            this.periods.forEach((period) => {
                this.calendarInformations.dates.push(this.datePipe.transform(period.start, 'yyyy-MM-dd'));

                let periodTreat = structuredClone(period);

                let date = periodTreat.start.setDate(periodTreat.start.getDate() + 1);

                while (date <= period.end.getTime()) {
                    this.calendarInformations.dates.push(this.datePipe.transform(date, 'yyyy-MM-dd'));
                    date = periodTreat.start.setDate(periodTreat.start.getDate() + 1);
                }
            });
        }

        this.calendarInformations.hours = [];

        if (this.hours.length > 0) {
            this.hours.forEach((period) => {
                this.calendarInformations.hours.push(`${this.datePipe.transform(period.start, 'HH:mm')}-${this.datePipe.transform(period.end, 'HH:mm')}`);
            });
        }
    }

    addDate() {
        this.dates.push(this.calendarForm.get('date').value);
        this.calendarForm.get('date').setValue(null);
    }

    deleteDate(date: Date) {
        this.dates.splice(this.dates.indexOf(date), 1);
    }

    addPeriod() {
        this.periods.push({
            start: this.calendarForm.get('startPeriod').value,
            end: this.calendarForm.get('endPeriod').value,
        });

        this.calendarForm.get('startPeriod').setValue(null);
        this.calendarForm.get('endPeriod').setValue(null);
    }

    deletePeriod(period: { start: Date; end: Date }) {
        this.periods.splice(this.periods.indexOf({ start: period.start, end: period.end }, 1));
    }

    addTime() {
        this.hours.push({
            start: this.calendarForm.get('startHour').value,
            end: this.calendarForm.get('endHour').value,
        });

        this.calendarForm.get('startHour').setValue(new Date(0, 0, 0, 0, 0));
        this.calendarForm.get('endHour').setValue(new Date(0, 0, 0, 1, 0));
    }

    deleteHours(period: { start: Date; end: Date }) {
        let index = this.hours.indexOf(this.hours.find((x) => x.start.getHours() === period.start.getHours() && x.end.getHours() === period.end.getHours()));

        this.hours.splice(index, 1);
    }

    allDaysChange(checked: boolean) {
        this.week.forEach((day) => (day.checked = checked));

        this.weekDaysCheckbox.checked = false;
        this.weekEndsCheckbox.checked = false;
    }

    weekDaysChange(checked: boolean) {
        this.week.find((d) => d.name === 'monday').checked = checked;
        this.week.find((d) => d.name === 'tuesday').checked = checked;
        this.week.find((d) => d.name === 'wednesday').checked = checked;
        this.week.find((d) => d.name === 'thursday').checked = checked;
        this.week.find((d) => d.name === 'friday').checked = checked;
        this.week.find((d) => d.name === 'saturday').checked = false;
        this.week.find((d) => d.name === 'sunday').checked = false;

        this.allDaysCheckbox.checked = false;
        this.weekEndsCheckbox.checked = false;
    }

    weekEndsChange(checked: boolean) {
        this.week.find((d) => d.name === 'monday').checked = false;
        this.week.find((d) => d.name === 'tuesday').checked = false;
        this.week.find((d) => d.name === 'wednesday').checked = false;
        this.week.find((d) => d.name === 'thursday').checked = false;
        this.week.find((d) => d.name === 'friday').checked = false;
        this.week.find((d) => d.name === 'saturday').checked = checked;
        this.week.find((d) => d.name === 'sunday').checked = checked;

        this.allDaysCheckbox.checked = false;
        this.weekDaysCheckbox.checked = false;
    }

    dayChange() {
        this.allDaysCheckbox.checked = false;
        this.weekDaysCheckbox.checked = false;
        this.weekEndsCheckbox.checked = false;
    }

    calendarTypeChange() {
        this.periods = [];
        this.dates = [];
        this.hours = [];
        this.allDays = true;
        this.week.forEach((day) => (day.checked = true));
    }
}
